import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FacilityRequestDto, type FacilityRequestStatus } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { statusLabel } from '@/components/ui/badge';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { type PickablePlan } from './PlanPicker';
import { useOwnerCapability } from './owner-nav';

const STATUS_TONE: Record<FacilityRequestStatus, 'warning' | 'secondary' | 'success' | 'destructive' | 'muted'> = {
  draft: 'secondary',
  submitted: 'warning',
  approved: 'success',
  rejected: 'destructive',
  expired: 'muted',
};

type Filter = 'open' | FacilityRequestStatus;

export function FacilityRequestsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canWrite = useOwnerCapability('facility_request.write');
  const canDecide = useOwnerCapability('facility_request.decide');
  const canProvision = useOwnerCapability('tenant.provision');
  const [filter, setFilter] = useState<Filter>('open');
  const [rejecting, setRejecting] = useState<FacilityRequestDto | null>(null);

  const plans = useQuery({ queryKey: ['plans'], queryFn: () => get<PickablePlan[]>('/platform/plans') });
  const planName = (id: string) => plans.data?.find((plan) => plan.id === id)?.name;

  const requests = useQuery({
    queryKey: ['facility-requests', filter],
    queryFn: () => get<FacilityRequestDto[]>(`/platform/facility-requests${qs({ status: filter === 'open' ? undefined : filter })}`),
  });

  const submit = useMutation({
    mutationFn: (id: string) => post(`/platform/facility-requests/${id}/submit`),
    onSuccess: () => {
      toast.success('Request sent to the Owner');
      void queryClient.invalidateQueries({ queryKey: ['facility-requests'] });
    },
    onError: (error) => toast.error('Could not send the request', describeError(error)),
  });

  const open = (row: FacilityRequestDto) => row.status === 'draft' || row.status === 'submitted';

  return (
    <>
      <PageHeader
        title="Facility requests"
        description="New facilities drafted by the team, waiting for the Owner to create them."
        actions={canWrite && <Button onClick={() => navigate('/owner/facility-requests/new')}>New request</Button>}
      />
      <SearchFilterBar
        filters={
          <FilterField label="Status">
            <Select className="w-56" value={filter} onChange={(event) => setFilter(event.target.value as Filter)}>
              <option value="open">Waiting (draft and sent)</option>
              <option value="approved">Created</option>
              <option value="rejected">Turned down</option>
              <option value="expired">Expired drafts</option>
            </Select>
          </FilterField>
        }
        onReset={filter !== 'open' ? () => setFilter('open') : undefined}
      />
      <DataTable
        rows={requests.data}
        loading={requests.isLoading} refreshing={requests.isFetching}
        error={requests.error}
        rowKey={(row) => row.id}
        empty="No requests here."
        columns={[
          {
            header: 'Facility',
            sort: (row) => row.name,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.name}</p>
                <p className="text-xs text-muted-foreground">
                  {row.planIds.map(planName).filter(Boolean).join(' + ') || humanize(row.facilityType, 'facility_type')} · {humanize(row.environment, 'tenant_environment')}
                  {row.offlineEnabled ? ' · Own server' : ''}
                </p>
                {row.duplicateOf && (
                  <Alert variant="warning" className="mt-1 py-1 text-xs">
                    Looks like {row.duplicateOf.name} — check before creating another.
                  </Alert>
                )}
              </div>
            ),
          },
          { header: 'Contact', sort: (row) => row.contactName, cell: (row) => [row.contactName, row.contactEmail, row.contactPhone].filter(Boolean).join(' · ') || '—' },
          { header: 'Asked by', sort: (row) => row.requestedByName, cell: (row) => row.requestedByName ?? '—' },
          { header: 'Updated', sort: (row) => row.updatedAt, cell: (row) => dateTime(row.updatedAt) },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <Badge variant={STATUS_TONE[row.status]}>{statusLabel(row.status, 'facility_request_status')}</Badge> },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              open(row) ? (
                <div className="flex justify-end gap-2">
                  {canWrite && row.status === 'draft' && (
                    <Button size="sm" variant="outline" onClick={() => submit.mutate(row.id)} loading={submit.isPending && submit.variables === row.id}>
                      Send
                    </Button>
                  )}
                  {canProvision && (
                    <Button size="sm" onClick={() => navigate(`/owner/tenants/new?requestId=${row.id}&returnTo=${encodeURIComponent('/owner/facility-requests')}`)}>
                      Create facility
                    </Button>
                  )}
                  {canDecide && (
                    <Button size="sm" variant="destructive" onClick={() => setRejecting(row)}>
                      Turn down
                    </Button>
                  )}
                </div>
              ) : row.decisionReason ? (
                <span className="text-xs text-muted-foreground">{row.decisionReason}</span>
              ) : null,
          },
        ]}
      />

      {rejecting && (
        <OwnerConfirmDialog
          open
          onClose={() => setRejecting(null)}
          title={`Turn down ${rejecting.name}`}
          description="The person who asked sees the reason you give."
          confirmLabel="Turn down"
          destructive
          successMessage="Request turned down"
          action={(payload) => post(`/platform/facility-requests/${rejecting.id}/reject`, payload)}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['facility-requests'] });
            setRejecting(null);
          }}
        />
      )}
    </>
  );
}
